import { Control, Operation } from '@yleisradio/areena-types';
import { useState, useEffect } from 'react';
import { isReference, isNavigator, isFunctionality } from '../typeGuards';
import {
  executeOperation,
  fetchControl,
} from '../../service/areena-api/fetchers';
import useSWR from 'swr';
import getRoutePath from '../Routes/getRoutePath';
import { useNavigateWithHistory } from '../Routes/useNavigateWithHistory';

const fetcher = (uri: string) => fetchControl(uri);

export const useReference = (control: Control | null) => {
  const [referenceControl, setReferenceControl] = useState<
    Control | undefined | null
  >();

  const reference = control && isReference(control) ? control : null;

  const key = reference?.destination?.uri;
  useSWR(key, fetcher, {
    onSuccess: controlResponse => {
      setReferenceControl(controlResponse?.data);
    },
  });
  return referenceControl;
};

export const useOperationAction = (
  operation: Operation | undefined,
  updateControl: (control: Control) => void
) => {
  const { mutate } = useSWR<ReturnType<typeof executeOperation>>(
    operation?.uri,
    null
  );

  const operationAction = async () => {
    if (!operation) return;
    return await mutate(() => executeOperation(operation));
  };

  const action = async () => {
    const controlResponse = await operationAction();
    if (controlResponse?.data) {
      updateControl(controlResponse.data);
    }
  };

  return operation ? action : undefined;
};

const useNavigateAction = (control: Control) => {
  const navigate = useNavigateWithHistory();

  if (isNavigator(control) || isFunctionality(control)) {
    const path = getRoutePath({
      pointer: control.destination,
      functionality: control.functionality,
    });

    const navigatorAction = path
      ? () => Promise.resolve(navigate(path))
      : undefined;
    return navigatorAction;
  }
};

const useControlAction = (
  control: Control,
  updateControl: (control: Control) => void
) => {
  const controlOperation = control.operation;
  const operationAction = useOperationAction(controlOperation, updateControl);
  const navigateAction = useNavigateAction(control);

  if (navigateAction) {
    return navigateAction;
  }

  if (operationAction) {
    return operationAction;
  }
};

export const useControl = (control: Control) => {
  const [actualControl, setActualControl] = useState<Control>(control);
  const controlByReference = useReference(control);

  useEffect(() => {
    if (controlByReference) {
      setActualControl(controlByReference);
    }
  }, [controlByReference]);

  const action = useControlAction(actualControl, setActualControl);

  return { actualControl, action };
};
